import React, { useEffect, useMemo, useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FaCalendarAlt, FaChartLine, FaBuilding, FaListUl } from 'react-icons/fa';
const PERIODS = [
  { key: 'week',    label: 'Week' },
  { key: 'month',   label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year',    label: 'Year' },
];

// ── Shared ECharts styling ─────────────────────────────────────────────────────
const TOOLTIP = {
  trigger: 'axis',
  axisPointer: { type: 'shadow' },
  backgroundColor: 'rgba(255,255,255,0.96)',
  borderColor: '#e2e8f0',
  borderWidth: 1,
  textStyle: { color: '#1e293b' },
};
const AXIS_LABEL = { color: '#64748b', fontSize: 11 };
const GRID = { left: '3%', right: '4%', bottom: '3%', top: '12%', containLabel: true };

// ── Summary stat card ───────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, from, to }) => (
  <Card bordered padding="none" className="tw-flex-1 tw-min-w-[160px] tw-overflow-hidden tw-relative">
    <div className="tw-p-4">
      <div className="tw-flex tw-items-start tw-justify-between tw-mb-3">
        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}>
          <Icon size={17} className="tw-text-white" />
        </div>
        <span className="tw-w-2.5 tw-h-2.5 tw-rounded-xl tw-mt-1" style={{ backgroundColor: from }} />
      </div>
      <h3 className="tw-text-2xl tw-font-black tw-text-slate-900 tw-mb-0.5 tw-leading-none">
        {Number(value || 0).toLocaleString()}
      </h3>
      <p className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-m-0">{label}</p>
    </div>
    <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-1" style={{ background: `linear-gradient(90deg, ${from}, ${to})` }} />
  </Card>
);
StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  icon: PropTypes.elementType.isRequired,
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
};

// ── Chart panel wrapper ───────────────────────────────────────────────────────
const ChartPanel = ({ icon: Icon, title, subtitle, children }) => (
  <Card bordered className="tw-h-full">
    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
      <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-slate-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
        <Icon size={15} className="tw-text-slate-600" />
      </div>
      <div>
        <h5 className="tw-text-sm tw-font-bold tw-text-slate-900 tw-m-0">{title}</h5>
        <p className="tw-text-xs tw-text-slate-400 tw-m-0">{subtitle}</p>
      </div>
    </div>
    {children}
  </Card>
);
ChartPanel.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const EmptyChart = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-[300px] tw-text-sm tw-text-slate-400">No data</div>
);

// ── Main ──────────────────────────────────────────────────────────────────────
const RequestStatsDashboard = ({ title, subtitle, Icon, accent, thunk, statusCards, categoryTitle, categorySubtitle }) => {
  const dispatch = useDispatch();
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear]     = useState(new Date().getFullYear());
  const [groupBy, setGroupBy] = useState('month');

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => current - i);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.resolve(dispatch(thunk({ year, group_by: groupBy, page: 1, per_page: 1 })))
      .then((res) => { if (!cancelled) setStats(res?.payload?.statistics || null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, groupBy, dispatch, thunk]);

  const trend    = useMemo(() => stats?.trend_data || [], [stats]);

  const offices  = stats?.office_data  || [];
  const category = stats?.category_data || [];

  const trendOption = useMemo(() => ({
    tooltip: TOOLTIP,
    grid: GRID,
    xAxis: {
      type: 'category',
      data: trend.map((d) => d.label),
      axisLine: { lineStyle: { color: '#cbd5e1' } },
      axisLabel: { ...AXIS_LABEL, rotate: trend.length > 14 ? 60 : 0 },
    },
    yAxis: {
      type: 'value', minInterval: 1,
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: AXIS_LABEL,
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
    },
    series: [{
      name: 'Requests', type: 'line', smooth: true,
      data: trend.map((d) => d.count),
      itemStyle: { color: accent }, lineStyle: { width: 3, color: accent },
      areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [
        { offset: 0, color: `${accent}40` }, { offset: 1, color: `${accent}05` },
      ] } },
    }],
  }), [trend, accent]);

  const barOption = (rows, color) => ({
    tooltip: { ...TOOLTIP, trigger: 'item' },
    grid: { ...GRID, left: '4%' },
    xAxis: { type: 'value', minInterval: 1, axisLabel: AXIS_LABEL, splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } } },
    yAxis: {
      type: 'category',
      data: rows.map((d) => d.label).reverse(),
      axisLine: { lineStyle: { color: '#cbd5e1' } },
      axisLabel: { ...AXIS_LABEL, width: 120, overflow: 'truncate' },
    },
    series: [{
      type: 'bar', barWidth: '55%',
      data: rows.map((d) => d.count).reverse(),
      itemStyle: { borderRadius: [0, 6, 6, 0], color },
    }],
  });

  const periodLabel = PERIODS.find((p) => p.key === groupBy)?.label || 'Month';

  return (
    <div className="tw-space-y-4">

      {/* Header */}
      <Card bordered>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-flex-wrap">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
              <Icon size={18} className="tw-text-white" />
            </div>
            <div>
              <h3 className="tw-text-base tw-font-bold tw-text-slate-900 tw-m-0">{title}</h3>
              <p className="tw-text-xs tw-text-slate-400 tw-m-0">{subtitle}</p>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            {/* Period toggle */}
            <div className="tw-inline-flex tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-0.5">
              {PERIODS.map((p) => (
                <button key={p.key} type="button" onClick={() => setGroupBy(p.key)}
                  className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-border-0 tw-cursor-pointer tw-transition-all ${
                    groupBy === p.key ? 'tw-bg-white tw-text-primary-600 tw-shadow-sm' : 'tw-bg-transparent tw-text-slate-500 hover:tw-text-slate-700'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
            {/* Year */}
            <div className="tw-flex tw-items-center tw-gap-2">
              <FaCalendarAlt size={13} className="tw-text-primary-500" />
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                className="tw-px-2.5 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded tw-text-slate-700 tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500 tw-cursor-pointer">
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>
      </Card>

      {loading && !stats ? (
        <div className="tw-flex tw-justify-center tw-py-16"><LoadingSpinner size="lg" /></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            {statusCards.map((c) => (
              <StatCard key={c.key} label={c.label} value={stats?.[c.key]} icon={c.icon} from={c.from} to={c.to} />
            ))}
          </div>

          {/* Trend */}
          <ChartPanel icon={FaChartLine} title="Request Trends" subtitle={`Requests submitted per ${periodLabel.toLowerCase()}`}>
            {trend.length === 0 ? <EmptyChart /> : (
              <ReactECharts option={trendOption} style={{ height: '320px', width: '100%' }} opts={{ renderer: 'svg' }} notMerge />
            )}
          </ChartPanel>

          {/* Top office + Top reason/department */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <ChartPanel icon={FaBuilding} title="Top Site Offices" subtitle="Requests by office location">
              {offices.length === 0 ? <EmptyChart /> : (
                <ReactECharts option={barOption(offices, '#6366f1')} style={{ height: '300px', width: '100%' }} opts={{ renderer: 'svg' }} notMerge />
              )}
            </ChartPanel>
            <ChartPanel icon={FaListUl} title={categoryTitle} subtitle={categorySubtitle}>
              {category.length === 0 ? <EmptyChart /> : (
                <ReactECharts option={barOption(category, '#0891b2')} style={{ height: '300px', width: '100%' }} opts={{ renderer: 'svg' }} notMerge />
              )}
            </ChartPanel>
          </div>
        </>
      )}
    </div>
  );
};

RequestStatsDashboard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  accent: PropTypes.string.isRequired,
  thunk: PropTypes.func.isRequired,
  statusCards: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
  })).isRequired,
  categoryTitle: PropTypes.string.isRequired,
  categorySubtitle: PropTypes.string.isRequired,
};

export default RequestStatsDashboard;
